import { from } from 'rxjs';
import { CancelBankAccountVerificationAttemptRequest } from '@square/dashboard-protos/models/squareup/deposits/bank_account_service';
import { getBankAccountType } from '@banking/ui/utils/checking/utils';

// State
// =============================================================================

export const INITIAL_STATE = {
  accountNumberSuffix: '',
  bankAccountToken: '',
  bankName: '',
  bankType: '',
  isCancellingVerification: false,
  isDone: false,
  isModalOpen: false,
  unitToken: ''
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["CancelVerificationReceived"] = "CANCEL_VERIFICATION_RECEIVED";
  ActionType["CancelVerificationRejected"] = "CANCEL_VERIFICATION_REJECTED";
  ActionType["InitCancelVerificationModal"] = "INIT_AND_OPEN_CANCEL_VERIFICATION_MODAL";
  ActionType["ToggleCancelVerificationModal"] = "TOGGLE_CANCEL_VERIFICATION_MODAL";
  ActionType["UserClickCancelBankVerification"] = "USER_CLICK_CANCEL_BANK_VERIFICATION";
  ActionType["UserClickContinueBankVerification"] = "USER_CLICK_CONTINUE_BANK_VERIFICATION";
  ActionType["WorkflowSetDone"] = "CANCEL_VERIFICATION/WORKFLOW_SET_DONE";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const cancelVerificationReceivedAction = response => ({
  type: ActionType.CancelVerificationReceived,
  response
});
export const cancelVerificationRejectedAction = error => ({
  type: ActionType.CancelVerificationRejected,
  error
});
export const initCancelVerificationModalAction = (unitToken, pendingBankAccount) => ({
  type: ActionType.InitCancelVerificationModal,
  unitToken,
  pendingBankAccount
});
export const toggleCancelVerificationModalAction = () => ({
  type: ActionType.ToggleCancelVerificationModal
});
export const userClickCancelBankVerificationAction = () => ({
  type: ActionType.UserClickCancelBankVerification
});
export const userClickContinueBankVerificationAction = () => ({
  type: ActionType.UserClickContinueBankVerification
});
export const workflowSetDone = () => ({
  type: ActionType.WorkflowSetDone
});

// Update
// =============================================================================

function handleToggleCancelVerificationModalAction(state) {
  const {
    isModalOpen
  } = state;
  if (isModalOpen === false) {
    return [{
      ...state,
      isModalOpen: true
    }, null];
  }

  // reset state on modal close
  return [INITIAL_STATE, null];
}
function handleCancelVerificationReceivedAction(context, state, action) {
  const {
    response
  } = action;
  const {
    accountNumberSuffix,
    bankType,
    bankName
  } = state;
  const nextState = {
    ...state,
    isCancellingVerification: false
  };
  if (response.error) {
    context.marketToasts.critical(context.intl.t('banking.unifiedBankAccounts.toasts.cancelVerification.error'));
    return [nextState, () => toggleCancelVerificationModalAction()];
  } else {
    context.marketToasts.warning(context.intl.t('banking.unifiedBankAccounts.toasts.cancelVerification.success', {
      bankName,
      bankType,
      accountNumberSuffix
    }));
    return [nextState, () => workflowSetDone()];
  }
}
function handleCancelVerificationRejectedAction(context, state, action) {
  context.marketToasts.critical(context.intl.t('banking.unifiedBankAccounts.toasts.unknownError'));
  context.sentry.captureException(action.error);
  return [{
    ...state,
    isCancellingVerification: false
  }, () => toggleCancelVerificationModalAction()];
}
function handleInitCancelVerificationModalAction(context, state, action) {
  const {
    unitToken,
    pendingBankAccount
  } = action;
  const {
    bankName,
    token,
    accountNumberSuffix,
    type
  } = pendingBankAccount;
  const {
    intl
  } = context;
  const bankType = getBankAccountType(type, intl);
  return [{
    ...state,
    accountNumberSuffix,
    bankAccountToken: token,
    bankName,
    bankType,
    unitToken
  }, () => toggleCancelVerificationModalAction()];
}
function handleUserClickCancelBankVerificationAction(context, state) {
  const {
    unitToken,
    bankAccountToken
  } = state;
  const work = () => {
    return from(context.bankAccountApi.cancelBankAccountVerificationAttempt(CancelBankAccountVerificationAttemptRequest.create({
      unitToken,
      bankAccountToken
    })).then(cancelVerificationReceivedAction, cancelVerificationRejectedAction));
  };
  return [{
    ...state,
    isCancellingVerification: true
  }, work];
}
function handleUserClickContinueBankVerificationAction(state) {
  return [state, () => toggleCancelVerificationModalAction()];
}
function handleWorkflowSetDone(state) {
  return [{
    ...state,
    isDone: true
  }, () => toggleCancelVerificationModalAction()];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.CancelVerificationReceived:
      return handleCancelVerificationReceivedAction(context, state, action);
    case ActionType.CancelVerificationRejected:
      return handleCancelVerificationRejectedAction(context, state, action);
    case ActionType.InitCancelVerificationModal:
      return handleInitCancelVerificationModalAction(context, state, action);
    case ActionType.ToggleCancelVerificationModal:
      return handleToggleCancelVerificationModalAction(state);
    case ActionType.UserClickCancelBankVerification:
      return handleUserClickCancelBankVerificationAction(context, state);
    case ActionType.UserClickContinueBankVerification:
      return handleUserClickContinueBankVerificationAction(state);
    case ActionType.WorkflowSetDone:
      return handleWorkflowSetDone(state);
  }
};